import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type {
  UpsertFavoriteListFormProps,
  UpsertFavoriteListFormVO,
} from './type'

import { AddCircleOutlinedIcon } from '@/components/icons/AddCircleOutlined'
import {
  Button,
  FormFieldText,
  FormFieldRadioGroup,
  ErrorMessageList,
  TextButton,
  FormFieldWrapper,
  Icon,
} from '@/components/ui'
import { ERROR_FIELD_NAMES } from '@/const'
import { useCreateFavorite, useUpdateFavorite } from '@/hooks/features/favorite'
import { favoriteKeyFactory } from '@/hooks/features/favorite/key'
import { useNotification } from '@/hooks/util'
import { queryClient } from '@/lib/react-query'
import { getErrorMessages } from '@/utils/text'

import { schema, formDefaultValues, DEFAULT_SELECTED_SALES_TYPE } from './const'
import { SalesTypeItem } from './SalesTypeItem'

export const UpsertFavoriteListForm = ({
  favorite,
  isDepositStore = false,
  onClickConfirm,
  onClickCancel,
}: UpsertFavoriteListFormProps) => {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const [errorMessages, setErrorMessages] = useState<string[]>([])

  const createFavorite = useCreateFavorite({})
  const updateFavorite = useUpdateFavorite({})

  const isLoading = useMemo(() => {
    return createFavorite.isLoading || updateFavorite.isLoading
  }, [createFavorite.isLoading, updateFavorite.isLoading])

  const formLabels = useMemo(() => {
    return {
      name: t('favorite.list.name'),
      isShared: t('favorite.list.shared'),
      salesTypeCodes: t('favorite.list.sales_type.title'),
    }
  }, [t])

  const isSharedOptions = useMemo(() => {
    return [
      {
        value: true,
        label: t('favorite.list.share'),
      },
      {
        value: false,
        label: t('favorite.list.not_share'),
      },
    ]
  }, [t])

  const { control, register, formState, handleSubmit, watch, setValue } =
    useForm<UpsertFavoriteListFormVO>({
      resolver: schema && zodResolver(schema),
      defaultValues: favorite
        ? {
            name: favorite.name,
            isShared: favorite.isShared,
            salesTypeCodes: favorite.salesTypes.map((obj) => obj.salesTypeCode),
          }
        : formDefaultValues,
    })

  const submitHandler = useCallback(
    async ({ name, isShared, salesTypeCodes }: UpsertFavoriteListFormVO) => {
      if (favorite) {
        setErrorMessages([])
        updateFavorite.mutate(
          {
            pathParams: [favorite.setNo, favorite.storeCode],
            data: {
              name,
              isShared,
              salesTypeCodes,
            },
          },
          {
            onSuccess: () => {
              onClickConfirm && onClickConfirm()
              notify({
                id: 'NOTIFICATION_MESSAGE.FAVORITE.UPDATE_SETTING',
                type: 'success',
                message: t('favorite.notification.update'),
              })
              queryClient.invalidateQueries(favoriteKeyFactory.list())
            },
            onError: (error) => {
              if (error.response?.status === 400) {
                setErrorMessages(
                  getErrorMessages({
                    fields: error.response.data.fields,
                    fieldNames: { ...ERROR_FIELD_NAMES, ...formLabels },
                  })
                )
              }
            },
          }
        )
      } else {
        setErrorMessages([])
        createFavorite.mutate(
          {
            data: {
              name,
              isShared,
              salesTypeCodes,
            },
          },
          {
            onSuccess: () => {
              onClickConfirm && onClickConfirm()
              notify({
                id: 'NOTIFICATION_MESSAGE.FAVORITE.CREATE',
                type: 'success',
                message: t('favorite.notification.create'),
              })
              queryClient.invalidateQueries(favoriteKeyFactory.list())
            },
            onError: (error) => {
              if (error.response?.status === 400) {
                setErrorMessages(
                  getErrorMessages({
                    fields: error.response.data.fields,
                    fieldNames: formLabels,
                  })
                )
              }
            },
          }
        )
      }
    },
    [
      favorite,
      updateFavorite,
      onClickConfirm,
      createFavorite,
      notify,
      formLabels,
      t,
    ]
  )

  const salesTypeCodes = watch('salesTypeCodes')
  const removeField = useCallback(
    (index) => {
      const arr = [...salesTypeCodes]
      arr.splice(index, 1)
      setValue('salesTypeCodes', arr)
    },
    [salesTypeCodes, setValue]
  )
  const appendField = useCallback(() => {
    const arr = [...salesTypeCodes, DEFAULT_SELECTED_SALES_TYPE]
    setValue('salesTypeCodes', arr)
  }, [salesTypeCodes, setValue])

  return (
    <div className={clsx('tw-px-1 tw-pb-4')}>
      {errorMessages.length > 0 && (
        <div className={clsx('tw-mt-4')}>
          <ErrorMessageList errorMessages={errorMessages} />
        </div>
      )}

      <form
        id="favorite-list-upsert-form"
        onSubmit={handleSubmit(submitHandler)}
      >
        <div className={clsx('tw-mt-4')}>
          <FormFieldText
            label={formLabels.name}
            placeholder={formLabels.name}
            disabled={isLoading}
            required
            error={formState.errors['name']}
            registration={register('name')}
          />
        </div>

        <div className={clsx('tw-mt-2')}>
          <div
            className={clsx(
              'tw-w-full',
              'tw-flex tw-flex-col tw-items-center',
              'tw-p-4',
              'tw-rounded',
              'tw-bg-primary-50'
            )}
          >
            <div className={clsx('tw-mb-2 tw-text-sm')}>
              {t('favorite.list.sales_type.title')}
            </div>
            <FormFieldWrapper
              error={formState.errors['salesTypeCodes']}
              showLabel={false}
            >
              <div
                className={clsx(
                  'tw-w-full',
                  'tw-flex tw-flex-col tw-items-center'
                )}
              >
                {salesTypeCodes.map((_salesTypeCode, index) => (
                  <div key={index}>
                    <SalesTypeItem
                      index={index}
                      control={control}
                      showRemove={index > 0}
                      onClickRemove={() => removeField(index)}
                    />
                  </div>
                ))}
              </div>
            </FormFieldWrapper>
            <div
              className={clsx(
                'tw-flex tw-items-center tw-justify-center',
                'hover:tw-opacity-hover',
                'tw-transition tw-duration-stateChange'
              )}
            >
              <TextButton variant="primary" onClick={appendField}>
                <Icon
                  icon={AddCircleOutlinedIcon}
                  size={'2xl'}
                  color={'primary'}
                />
                <span className={clsx('tw-ml-1 tw-text-sm')}>
                  {t('favorite.list.sales_type.add')}
                </span>
              </TextButton>
            </div>
          </div>
        </div>

        {isDepositStore && (
          <div className={clsx('tw-mt-2')}>
            <div
              className={clsx(
                'tw-w-full',
                'tw-flex tw-flex-col tw-items-center',
                'tw-p-4',
                'tw-rounded',
                'tw-bg-primary-50'
              )}
            >
              <p className={clsx('tw-text-center tw-text-sm')}>
                {t('favorite.list.shared')}
              </p>
              <div className={clsx('tw-mt-1')}>
                <FormFieldRadioGroup
                  showLabel={false}
                  label={formLabels.isShared}
                  error={formState.errors['isShared']}
                  name="isShared"
                  control={control}
                  options={isSharedOptions}
                  formName="isShared"
                  direction={'row'}
                  gap={'lg'}
                />
              </div>
            </div>
          </div>
        )}

        <div className={clsx('tw-mt-4')}>
          <div
            className={clsx(
              'tw-flex tw-items-center tw-justify-center',
              'tw-gap-2'
            )}
          >
            <Button
              type="button"
              variant="normal"
              disabled={isLoading}
              onClick={onClickCancel}
            >
              {t('favorite.modal.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading} isLoading={isLoading}>
              {t('favorite.modal.save')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
